import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useUrlState, useDebounce } from '@/shared/lib';
import { compareFilters, isDefaultFilters } from '@/shared/lib/utils';

import type { ReceiveHistoryUrlState } from './types';
import { DEFAULT_URL_STATE } from './constants';
import { buildReceiveHistoryFilterChips } from './build-filter-chips';

/** Non-filter keys carried by the URL state (excluded from dirty/default checks). */
const NON_FILTER_KEYS: (keyof ReceiveHistoryUrlState)[] = ['search', 'sortBy', 'sortDir', 'size'];

/**
 * Receive History filter state — local draft vs committed URL state, mirroring
 * `useReceiveFilters` / `useConsignmentsFilters`. The header search commits
 * directly (300ms debounce, bypasses Apply); the panel filters (consignment
 * number + received-date range) commit on Apply.
 */
export const useReceiveHistoryFilters = () => {
  const { t } = useTranslation();
  const [urlState, setUrlState, resetUrlState] = useUrlState(DEFAULT_URL_STATE);
  const [localFilters, setLocalFilters] = useState<ReceiveHistoryUrlState>(() => urlState);
  const [searchQuery, setSearchQuery] = useState(() => urlState.search);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const activeFiltersCount = useMemo(() => {
    let count = 0;
    if (urlState.consignmentNumber) count++;
    if (urlState.dateFrom || urlState.dateTo) count++;
    return count;
  }, [urlState]);

  const hasActiveFilters = activeFiltersCount > 0;

  const isFiltersDirty = useMemo(
    () => !compareFilters(localFilters, urlState, NON_FILTER_KEYS),
    [localFilters, urlState]
  );
  const hasDefaultFilters = useMemo(() => isDefaultFilters(urlState, DEFAULT_URL_STATE, NON_FILTER_KEYS), [urlState]);

  const handleFilterChange = useCallback((key: keyof ReceiveHistoryUrlState, value: string) => {
    setLocalFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleDateRangeChange = useCallback((dateFrom: string, dateTo: string) => {
    setLocalFilters((prev) => ({ ...prev, dateFrom, dateTo }));
  }, []);

  const handleSearchChange = useCallback((query: string) => {
    setSearchQuery(query);
  }, []);

  const handleReset = useCallback(() => {
    setLocalFilters(DEFAULT_URL_STATE);
    setSearchQuery('');
    resetUrlState();
  }, [resetUrlState]);

  const removeFilter = useCallback(
    (key: string) => {
      if (key === 'date') {
        setUrlState({ dateFrom: DEFAULT_URL_STATE.dateFrom, dateTo: DEFAULT_URL_STATE.dateTo });
        return;
      }
      setUrlState({ [key]: DEFAULT_URL_STATE[key as keyof ReceiveHistoryUrlState] });
    },
    [setUrlState]
  );

  const activeFilters = useMemo(
    () => buildReceiveHistoryFilterChips({ state: urlState, t, onRemove: removeFilter }),
    [urlState, t, removeFilter]
  );

  const handleApplyFilters = useCallback(() => {
    const { search, ...filtersWithoutSearch } = localFilters;
    setUrlState(filtersWithoutSearch);
  }, [localFilters, setUrlState]);

  useEffect(() => {
    setLocalFilters(urlState);
  }, [urlState]);

  useEffect(() => {
    if (debouncedSearchQuery !== urlState.search) {
      setUrlState({ search: debouncedSearchQuery });
    }
  }, [debouncedSearchQuery, setUrlState, urlState.search]);

  return {
    urlState: localFilters,
    activeFilters,
    searchQuery,
    activeFiltersCount,
    hasActiveFilters,
    isFiltersDirty,
    hasDefaultFilters,

    handleFilterChange,
    handleDateRangeChange,
    handleSearchChange,
    handleReset,
    handleApplyFilters
  };
};
